// src/pages/AdminTienda.js
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  actualizarEstadoReserva,
  actualizarPagoReserva,
  listarChatAdmin,
  listarClientesChatAdmin,
  listarReservas,
  responderChatAdmin,
  solicitarCodigoAdmin,
  verificarCodigoAdmin,
} from "../lib/tiendaApi";

const ESTADOS = ["solicitado", "en_transito", "a_recoger", "entregado"];

const ESTADO_LABELS = {
  solicitado: "Solicitado",
  en_transito: "En tránsito",
  a_recoger: "A recoger",
  entregado: "Entregado",
};

// Login por "código al correo": pide el código al backend (que lo envía por
// email si la cuenta está autorizada) y lo verifica contra el servidor.
const AccessGate = ({ onUnlocked }) => {
  const [email, setEmail] = useState("");
  const [codeRequested, setCodeRequested] = useState(false);
  const [inputCode, setInputCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Escribe tu email.");
      return;
    }
    try {
      setLoading(true);
      setError("");
      await solicitarCodigoAdmin(email.trim());
      setCodeRequested(true);
    } catch (err) {
      setError(err.message || "No se pudo solicitar el código.");
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError("");
      await verificarCodigoAdmin(email.trim(), inputCode.trim());
      onUnlocked();
    } catch (err) {
      setError(err.message || "Código incorrecto o caducado.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
      <main style={{ padding: 40, textAlign: "center", maxWidth: 360, margin: "0 auto" }}>
        <h1 style={{ marginBottom: 20, color: "#0f172a" }}>Panel de la tienda</h1>

        {!codeRequested ? (
          <form onSubmit={requestCode} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <input
              type="email"
              placeholder="Tu email de administrador"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ padding: 10, borderRadius: 8, border: "1px solid #ccc" }}
            />
            <button
              type="submit"
              disabled={loading}
              style={{ padding: "10px 20px", borderRadius: 8, background: "#001d7e", color: "white", border: "none", cursor: "pointer" }}
            >
              {loading ? "Enviando…" : "Enviar código"}
            </button>
          </form>
        ) : (
          <form onSubmit={verifyCode} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <p style={{ fontSize: 13, color: "#555" }}>
              Si tu email está autorizado, te ha llegado un código por correo.
              Caduca en 10 minutos.
            </p>
            <input
              type="text"
              placeholder="Introduce el código"
              value={inputCode}
              onChange={(e) => setInputCode(e.target.value)}
              style={{ padding: 10, borderRadius: 8, border: "1px solid #ccc" }}
            />
            <button
              type="submit"
              disabled={loading}
              style={{ padding: "10px 20px", borderRadius: 8, background: "#001d7e", color: "white", border: "none", cursor: "pointer" }}
            >
              {loading ? "Comprobando…" : "Entrar"}
            </button>
          </form>
        )}

        {error && <p style={{ color: "red", marginTop: 10 }}>{error}</p>}
      </main>
    </div>
  );
};

const Pedidos = ({ reservations, onCambiarEstado, onCambiarPago }) => {
  const [busqueda, setBusqueda] = useState("");

  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return reservations;
    return reservations.filter((r) =>
      [r.codigo, r.nombre, r.email, r.articulo].some((campo) =>
        campo?.toLowerCase().includes(q)
      )
    );
  }, [reservations, busqueda]);

  return (
    <>
      <input
        type="text"
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        placeholder="Buscar por código, nombre, email o artículo…"
        style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #ccc", marginBottom: 20 }}
      />

      {filtradas.length === 0 ? (
        <p style={{ textAlign: "center", color: "#666" }}>No hay pedidos que coincidan.</p>
      ) : (
        filtradas.map((r) => (
          <section
            key={r.codigo}
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 16,
              padding: 16,
              marginBottom: 16,
              backgroundColor: "#f7f7f7",
              borderRadius: 12,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 20, letterSpacing: 2, color: "#001d7e" }}>
              {r.codigo}
            </div>

            <div style={{ flex: "1 1 180px" }}>
              <p style={{ margin: 0, fontWeight: 600 }}>{r.nombre}</p>
              <p style={{ margin: "2px 0", fontSize: 13, color: "#555" }}>
                {r.email}
                {r.telefono ? ` · ${r.telefono}` : ""}
              </p>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>{r.articulo}</p>
              <p style={{ margin: 0, fontSize: 13 }}>
                Talla {r.talla} · Cantidad {r.cantidad}
              </p>
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, whiteSpace: "nowrap" }}>
              <input
                type="checkbox"
                checked={!!r.pagado}
                onChange={(e) => onCambiarPago(r.codigo, e.target.checked)}
              />
              {r.pagado ? "Pagado" : "Sin pagar"}
            </label>

            <select
              value={r.estado}
              onChange={(e) => onCambiarEstado(r.codigo, e.target.value)}
              style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc" }}
            >
              {ESTADOS.map((estado) => (
                <option key={estado} value={estado}>
                  {ESTADO_LABELS[estado]}
                </option>
              ))}
            </select>
          </section>
        ))
      )}
    </>
  );
};

const ChatAdmin = ({ reservations }) => {
  const [clientes, setClientes] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [seleccionado, setSeleccionado] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [respuesta, setRespuesta] = useState("");
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  const cargarClientes = useCallback(async () => {
    try {
      const data = await listarClientesChatAdmin();
      setClientes(data.clientes || []);
    } catch (err) {
      setError(err.message || "No se pudieron cargar las conversaciones.");
    }
  }, []);

  useEffect(() => {
    cargarClientes();
  }, [cargarClientes]);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return clientes;

    // Si el texto coincide con el código de un pedido, filtramos por el
    // email del cliente dueño de ese pedido.
    const pedidoCoincide = reservations.find((r) => r.codigo.toLowerCase().includes(q));

    return clientes.filter((c) => {
      const porDatosCliente =
        c.nombre?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q);
      const porPedido = pedidoCoincide && c.email === pedidoCoincide.email;
      return porDatosCliente || porPedido;
    });
  }, [clientes, busqueda, reservations]);

  const abrirChat = async (cliente) => {
    setSeleccionado(cliente);
    try {
      const data = await listarChatAdmin(cliente.id);
      setMensajes(data.mensajes || []);
    } catch (err) {
      setError(err.message || "No se pudo cargar la conversación.");
    }
  };

  const enviarRespuesta = async (e) => {
    e.preventDefault();
    if (!respuesta.trim() || !seleccionado) return;

    try {
      setEnviando(true);
      await responderChatAdmin(seleccionado.id, respuesta.trim());
      setRespuesta("");
      const data = await listarChatAdmin(seleccionado.id);
      setMensajes(data.mensajes || []);
      cargarClientes();
    } catch (err) {
      setError(err.message || "No se pudo enviar la respuesta.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <input
        type="text"
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        placeholder="Buscar por cliente, email o código de pedido…"
        style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #ccc", marginBottom: 20 }}
      />

      {error && <p style={{ textAlign: "center", color: "red" }}>{error}</p>}

      {filtrados.length === 0 ? (
        <p style={{ textAlign: "center", color: "#666" }}>No hay conversaciones que coincidan.</p>
      ) : (
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 220px", display: "flex", flexDirection: "column", gap: 8 }}>
            {filtrados.map((c) => (
              <button
                key={c.id}
                onClick={() => abrirChat(c)}
                style={{
                  textAlign: "left",
                  padding: 12,
                  borderRadius: 10,
                  border: seleccionado?.id === c.id ? "2px solid #001d7e" : "1px solid #e2e8f0",
                  background: "white",
                  cursor: "pointer",
                }}
              >
                <p style={{ margin: 0, fontWeight: 600 }}>{c.nombre}</p>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: "#666" }}>{c.email}</p>
                {c.ultimo_mensaje && (
                  <p style={{ margin: "4px 0 0", fontSize: 12, color: "#888" }}>
                    {c.ultimo_mensaje.length > 40
                      ? `${c.ultimo_mensaje.slice(0, 40)}…`
                      : c.ultimo_mensaje}
                  </p>
                )}
              </button>
            ))}
          </div>

          <div style={{ flex: "2 1 320px", background: "#f7f7f7", borderRadius: 12, padding: 16 }}>
            {!seleccionado ? (
              <p style={{ color: "#666" }}>Elige una conversación de la lista.</p>
            ) : (
              <>
                <p style={{ fontWeight: 600, marginBottom: 12 }}>
                  {seleccionado.nombre} <span style={{ fontWeight: 400, color: "#666" }}>({seleccionado.email})</span>
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 280, overflowY: "auto", marginBottom: 12 }}>
                  {mensajes.map((m, i) => (
                    <div
                      key={i}
                      style={{
                        alignSelf: m.autor === "admin" ? "flex-end" : "flex-start",
                        background: m.autor === "admin" ? "#001d7e" : "white",
                        color: m.autor === "admin" ? "white" : "#0f172a",
                        borderRadius: 10,
                        padding: "8px 12px",
                        maxWidth: "80%",
                        fontSize: 14,
                      }}
                    >
                      {m.mensaje}
                    </div>
                  ))}
                </div>

                <form onSubmit={enviarRespuesta} style={{ display: "flex", gap: 8 }}>
                  <input
                    type="text"
                    value={respuesta}
                    onChange={(e) => setRespuesta(e.target.value)}
                    placeholder="Escribe tu respuesta…"
                    style={{ flex: 1, padding: 10, borderRadius: 8, border: "1px solid #ccc" }}
                  />
                  <button
                    type="submit"
                    disabled={enviando}
                    style={{ padding: "10px 16px", borderRadius: 8, background: "#001d7e", color: "white", border: "none", cursor: "pointer" }}
                  >
                    Responder
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default function AdminTienda() {
  const [unlocked, setUnlocked] = useState(false);
  const [tab, setTab] = useState("pedidos"); // "pedidos" | "chats"
  const [reservations, setReservations] = useState([]);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      setError("");
      const data = await listarReservas();
      setReservations(data.reservas || []);
    } catch (err) {
      setError(err.message || "No se pudieron cargar los pedidos.");
    }
  }, []);

  useEffect(() => {
    if (unlocked) refresh();
  }, [unlocked, refresh]);

  if (!unlocked) {
    return <AccessGate onUnlocked={() => setUnlocked(true)} />;
  }

  const cambiarEstado = async (codigo, estado) => {
    try {
      await actualizarEstadoReserva(codigo, estado);
      await refresh();
    } catch (err) {
      setError(err.message || "No se pudo actualizar el estado.");
    }
  };

  const cambiarPago = async (codigo, pagado) => {
    try {
      await actualizarPagoReserva(codigo, pagado);
      await refresh();
    } catch (err) {
      setError(err.message || "No se pudo actualizar el pago.");
    }
  };

  const tabStyle = (activo) => ({
    flex: 1,
    padding: "10px 0",
    textAlign: "center",
    fontWeight: 600,
    cursor: "pointer",
    background: activo ? "#001d7e" : "white",
    color: activo ? "white" : "#0f172a",
    border: "1px solid #001d7e",
  });

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
      <main style={{ padding: 20, maxWidth: 900, margin: "0 auto" }}>
        <h1 style={{ textAlign: "center", marginBottom: 20, color: "#0f172a" }}>
          Panel de la tienda
        </h1>

        <div style={{ display: "flex", borderRadius: 8, overflow: "hidden", marginBottom: 24 }}>
          <div style={tabStyle(tab === "pedidos")} onClick={() => setTab("pedidos")}>
            Pedidos
          </div>
          <div style={tabStyle(tab === "chats")} onClick={() => setTab("chats")}>
            Chats
          </div>
        </div>

        {error && <p style={{ textAlign: "center", color: "red", marginBottom: 20 }}>{error}</p>}

        {tab === "pedidos" ? (
          <Pedidos
            reservations={reservations}
            onCambiarEstado={cambiarEstado}
            onCambiarPago={cambiarPago}
          />
        ) : (
          <ChatAdmin reservations={reservations} />
        )}
      </main>
    </div>
  );
}
